import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PackagePlus, Upload, Trash2, AlertTriangle, Plus, Check, ChevronDown, Package, Camera, ShieldCheck, Smartphone, FolderOpen } from 'lucide-react'
import { CameraScan } from '../components/CameraScan'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useActiveTenant } from '../contexts/tenant'
import { useAuth } from '../contexts/auth'
import { cn } from '../lib/cn'

/** "2028-04-01" → "01/04/2028" */
function fmtDate(iso: string | null): string {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso
}

type Prod = { productId: string; sku: string; name: string; saldo: number }
type Linha = { sku: string; quantity: number }

type Allocation = {
  palletItemId: string
  palletId: string
  palletCode: string
  lotId: string | null
  lotNumber: string | null
  expiryDate: string | null
  boxes: number
}
type PlanRow = {
  sku: string
  productId: string | null
  name: string | null
  unitsPerBox: number | null
  demanda: number
  fracionamento: number
  deficit: number
  caixasSugeridas: number
  unidadesMovidas: number
  sobraResultante: number
  allocations: Allocation[]
  shortBoxes: number
  warning: 'not_found' | 'no_units_per_box' | 'insufficient_reserve' | null
}
type RegularizacaoLinha = {
  productId: string; sku: string; name: string | null; unitsPerBox: number | null
  saldo: number; faltam: number; caixas: number; unidades: number; saldoFinal: number
  allocations: (Allocation & { sequencia?: number | null; motivo?: string; aberto?: boolean })[]
  semLastro: number; aviso: string | null
}

type Consolidado = {
  productId: string; sku: string; name: string; unitsPerBox: number | null
  fracionamento: number; reservaCaixas: number; reservaUnidades: number
  total: number; nearestExpiry: string | null
}

/** Mesmo parser da Baixa por separação (PDF com colunas grudadas). */
function parseGuia(text: string): Linha[] {
  let region = text
  const hdr = /Conferido|Unidades/i.exec(region)
  if (hdr) region = region.slice(hdr.index + hdr[0].length)
  const end = region.search(/TOTAL/i)
  if (end >= 0) region = region.slice(0, end)
  const out: Linha[] = []
  for (const seg of region.split(/\[\s*\]/)) {
    const nums = seg.match(/\d+/g)
    if (!nums || nums.length < 2) continue
    const sku = nums[0]
    const quantity = Number(nums[nums.length - 1])
    if (quantity > 0 && Number(sku) < 100000) out.push({ sku, quantity })
  }
  return out
}

const WARN: Record<NonNullable<PlanRow['warning']>, { label: string; variant: 'danger' | 'warning' }> = {
  not_found:            { label: 'SKU não encontrado',       variant: 'danger' },
  no_units_per_box:     { label: 'Sem unidades/caixa',       variant: 'warning' },
  insufficient_reserve: { label: 'Reserva insuficiente',     variant: 'warning' },
}

export function AbastecimentoPage() {
  const tenantId = useActiveTenant()
  const [tab, setTab] = useState<'plano' | 'consolidado' | 'negativos'>('plano')

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl text-gray-900">Abastecimento</h2>
        <p className="text-sm text-gray-500">Toda manhã você traz caixas fechadas do <b>armazém</b> para o <b>fracionamento</b> (onde se separa). O WsFull calcula quantas caixas trazer com base no que saiu, pelo lote que vence primeiro (FEFO). O <b>estoque de venda é o total das duas salas</b>.</p>
      </div>

      <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit">
        {(['plano', 'consolidado', 'negativos'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={
              'rounded-md px-4 py-1.5 text-sm font-medium transition-colors ' +
              (tab === t ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700')
            }
          >
            {t === 'plano' ? 'Plano de abastecimento' : t === 'consolidado' ? 'Saldo por sala' : 'Fracionamento negativo'}
          </button>
        ))}
      </div>

      {tab === 'plano' ? <PlanoTab tenantId={tenantId} />
        : tab === 'consolidado' ? <ConsolidadoTab tenantId={tenantId} />
        : <NegativosTab tenantId={tenantId} />}
    </div>
  )
}

// ── Aba: Plano de abastecimento ────────────────────────────────────────────────

function PlanoTab({ tenantId }: { tenantId: string }) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [demand, setDemand] = useState<Linha[]>([])
  const [reference, setReference] = useState('')
  const [plan, setPlan] = useState<PlanRow[] | null>(null)
  const [extracting, setExtracting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [newSku, setNewSku] = useState('')
  const [newQty, setNewQty] = useState('')
  const [showCovered, setShowCovered] = useState(false)
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set())
  const [scanFor, setScanFor] = useState<{ palletId: string; palletCode: string } | null>(null)
  const [scanStatus, setScanStatus] = useState<{ text: string; tone: 'ok' | 'warn' | 'err' } | null>(null)
  const [loadedPlanId, setLoadedPlanId] = useState<string | null>(null)
  const [savedCode, setSavedCode] = useState<string | null>(null)
  const [savingPlan, setSavingPlan] = useState(false)
  const qc = useQueryClient()

  const { data: prods = [] } = useQuery<Prod[]>({
    queryKey: ['count-sheet', tenantId],
    queryFn: async () => (await api.get('/stock/count-sheet', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })
  const bySku = useMemo(() => new Map(prods.map(p => [p.sku.trim(), p])), [prods])

  type SavedPlan = { id: string; code: string; reference: string | null; itemCount: number; createdAt: string }
  const { data: savedPlans = [] } = useQuery<SavedPlan[]>({
    queryKey: ['replen-plans', tenantId],
    queryFn: async () => (await api.get('/replenishment/plans', { params: { tenantId, status: 'open' } })).data,
    enabled: !!tenantId,
    refetchInterval: 15000,
  })

  async function salvarPlano() {
    if (!plan || savingPlan) return
    setSavingPlan(true); setMsg(null); setSavedCode(null)
    try {
      const { data } = await api.post('/replenishment/plans', { tenantId, reference, rows: plan })
      setSavedCode(data.code)
      setLoadedPlanId(data.id)
      await qc.invalidateQueries({ queryKey: ['replen-plans', tenantId] })
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao salvar o plano')
    } finally { setSavingPlan(false) }
  }

  async function abrirPlano(id: string) {
    setMsg(null); setSavedCode(null)
    try {
      const { data } = await api.get(`/replenishment/plans/${id}`)
      setPlan(data.rows as PlanRow[])
      setReference(data.reference ?? '')
      setLoadedPlanId(id)
      setConfirmed(new Set())
      setDemand([])
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao abrir o plano')
    }
  }

  async function descartarPlano(id: string) {
    try {
      await api.post(`/replenishment/plans/${id}/cancel`)
      await qc.invalidateQueries({ queryKey: ['replen-plans', tenantId] })
      if (loadedPlanId === id) { setLoadedPlanId(null); setPlan(null) }
    } catch { /* ignore */ }
  }

  function carregar(text: string) {
    setDemand(parseGuia(text)); setPlan(null)
    const m = /SEP-\d{8}-\d+/i.exec(text); if (m) setReference(m[0])
  }

  async function uploadRelatorio(file: File) {
    setExtracting(true); setMsg(null); setPlan(null)
    try {
      const isJson = file.name.toLowerCase().endsWith('.json') || file.type === 'application/json'
      if (isJson) {
        const data = JSON.parse(await file.text())
        const its: Linha[] = (data.itens ?? [])
          .map((i: any) => ({ sku: String(i.sku).trim(), quantity: Number(i.quantidade ?? i.quantity) }))
          .filter((i: Linha) => i.quantity > 0)
        setDemand(its)
        if (data.separacao) setReference(String(data.separacao))
      } else {
        const b64 = await new Promise<string>((res, rej) => {
          const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(file)
        })
        const { data } = await api.post('/stock/extract-report', { fileBase64: b64 })
        carregar(data.text ?? '')
      }
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao ler o arquivo')
    } finally { setExtracting(false) }
  }

  function addManual() {
    const sku = newSku.trim(); const q = Number(newQty)
    if (!sku || !(q > 0)) return
    setDemand(arr => [...arr, { sku, quantity: q }]); setPlan(null)
    setNewSku(''); setNewQty('')
  }

  async function calcular() {
    const valid = demand.filter(d => d.quantity > 0)
    if (!valid.length || busy) return
    setBusy(true); setMsg(null)
    try {
      const { data } = await api.post('/replenishment/plan', { tenantId, items: valid })
      setPlan(data as PlanRow[])
      setConfirmed(new Set())
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao calcular o plano')
    } finally { setBusy(false) }
  }

  function setBoxes(rowIdx: number, allocIdx: number, boxes: number) {
    setPlan(rows => {
      if (!rows) return rows
      return rows.map((r, i) => {
        if (i !== rowIdx) return r
        const allocations = r.allocations.map((a, j) => j === allocIdx ? { ...a, boxes: Math.max(0, boxes) } : a)
        const upc = r.unitsPerBox ?? 0
        const caixas = allocations.reduce((s, a) => s + a.boxes, 0)
        const movidas = caixas * upc
        return { ...r, allocations, caixasSugeridas: caixas, unidadesMovidas: movidas, sobraResultante: r.fracionamento + movidas - r.demanda }
      })
    })
  }

  const applyAllocations = useMemo(() => {
    if (!plan) return []
    return plan.flatMap(r =>
      r.productId && r.unitsPerBox
        ? r.allocations.filter(a => a.boxes > 0).map(a => ({
            palletItemId: a.palletItemId, productId: r.productId as string,
            lotId: a.lotId, boxes: a.boxes, unitsPerBox: r.unitsPerBox as number,
          }))
        : []
    )
  }, [plan])

  const totalCaixas = applyAllocations.reduce((s, a) => s + a.boxes, 0)
  const totalUnidades = applyAllocations.reduce((s, a) => s + a.boxes * a.unitsPerBox, 0)

  // Paletes que precisam ser conferidos (QR) antes de confirmar o abastecimento.
  const pendingPallets = useMemo(() => {
    const ids = new Set<string>()
    plan?.forEach(r => r.allocations.forEach(a => { if (a.boxes > 0) ids.add(a.palletId) }))
    return ids
  }, [plan])
  // Admin não precisa bipar o QR (trabalha no desktop, sem câmera); operador/agente sim.
  const allConfirmed = isAdmin || [...pendingPallets].every(id => confirmed.has(id))

  function onScanPallet(text: string) {
    if (!scanFor) return
    if (text.trim() === scanFor.palletId) {
      setConfirmed(s => new Set(s).add(scanFor.palletId))
      setScanStatus({ text: `✓ Palete ${scanFor.palletCode} conferido`, tone: 'ok' })
      setTimeout(() => setScanFor(null), 800)
    } else {
      setScanStatus({ text: `Palete errado! Esperado ${scanFor.palletCode}`, tone: 'err' })
    }
  }

  async function confirmar() {
    if (!applyAllocations.length || busy) return
    if (!allConfirmed) { setMsg('Confira o QR de todos os paletes (📷) antes de confirmar.'); return }
    if (!window.confirm(`Descer ${totalCaixas} caixa(s) (${totalUnidades} un) dos paletes para o fracionamento?`)) return
    setBusy(true); setMsg(null)
    try {
      const url = loadedPlanId ? `/replenishment/plans/${loadedPlanId}/apply` : '/replenishment/apply'
      const { data: r } = await api.post(url, { tenantId, allocations: applyAllocations })
      setMsg(`Abastecimento aplicado: ${r.caixas} caixa(s), ${r.unidades} unidade(s) em ${r.itens} movimento(s).`)
      setPlan(null); setDemand([]); setConfirmed(new Set())
      setLoadedPlanId(null); setSavedCode(null)
      await qc.invalidateQueries({ queryKey: ['replen-plans', tenantId] })
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao aplicar abastecimento')
    } finally { setBusy(false) }
  }

  const demandaOk = demand.filter(d => bySku.has(d.sku.trim())).length

  return (
    <div className="space-y-4">
      {msg && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">{msg}</div>}

      {savedCode && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          Plano salvo como <b className="font-mono">{savedCode}</b>. No <b>celular</b>, abra <b>Abastecimento</b> → <b>Planos salvos</b> e faça a conferência dos paletes com a câmera.
        </div>
      )}

      {savedPlans.length > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
              <Smartphone className="h-4 w-4 text-blue-600" /> Planos salvos (abrir no celular)
            </div>
            <ul className="divide-y divide-gray-100">
              {savedPlans.map(sp => (
                <li key={sp.id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="font-mono font-bold text-gray-900">{sp.code}</span>
                  <span className="flex-1 text-gray-600">
                    {sp.reference || 'sem ref.'} · {sp.itemCount} item(ns)
                    {loadedPlanId === sp.id && <span className="ml-2 text-blue-600">(aberto)</span>}
                  </span>
                  <Button size="sm" variant="secondary" onClick={() => abrirPlano(sp.id)}>
                    <FolderOpen className="h-4 w-4" /> Abrir
                  </Button>
                  <button className="text-gray-300 hover:text-red-500" title="Descartar" onClick={() => descartarPlano(sp.id)}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="grid gap-3 lg:grid-cols-2">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-3 py-6 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600">
              <Upload className="h-4 w-4" />
              {extracting ? 'Lendo…' : 'Enviar separação (JSON recomendado, ou PDF)'}
              <input type="file" accept="application/json,.json,application/pdf" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadRelatorio(f); e.currentTarget.value = '' }} />
            </label>
            <div className="space-y-2">
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Referência (ex: SEP-20260710-01)"
                value={reference} onChange={e => setReference(e.target.value)}
              />
              <div className="flex gap-2">
                <input className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="SKU"
                  value={newSku} onChange={e => setNewSku(e.target.value)} onKeyDown={e => e.key === 'Enter' && addManual()} />
                <input className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Qtd" type="number" min={1}
                  value={newQty} onChange={e => setNewQty(e.target.value)} onKeyDown={e => e.key === 'Enter' && addManual()} />
                <Button variant="secondary" size="md" onClick={addManual}><Plus className="h-4 w-4" /> Adicionar</Button>
              </div>
            </div>
          </div>

          {demand.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {demand.map((d, i) => {
                const p = bySku.get(d.sku.trim())
                return (
                  <span key={i} className={'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ' + (p ? 'bg-gray-100 text-gray-700' : 'bg-red-50 text-red-600')}>
                    <span className="font-mono font-medium">{d.sku}</span>
                    <span className="text-gray-400">×</span>{d.quantity}
                    <button onClick={() => { setDemand(arr => arr.filter((_, j) => j !== i)); setPlan(null) }} className="ml-0.5 text-gray-400 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                  </span>
                )
              })}
              <Button size="sm" className="ml-auto" loading={busy} disabled={!demandaOk} onClick={calcular}>
                <PackagePlus className="h-4 w-4" /> Calcular abastecimento
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {plan && (() => {
        const actionRows = plan.map((r, ri) => ({ r, ri })).filter(x => x.r.deficit > 0)
        const coveredRows = plan.filter(r => r.deficit <= 0)
        return (
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-blue-50/40 px-4 py-3">
                <div>
                  <p className="text-sm font-bold text-gray-900">🏭 Buscar no armazém</p>
                  <p className="text-xs text-gray-500">{totalCaixas} caixa(s) · {totalUnidades} unidades</p>
                </div>
                <div className="flex items-center gap-2">
                  {!loadedPlanId && applyAllocations.length > 0 && (
                    <Button variant="secondary" loading={savingPlan} onClick={salvarPlano} title="Salvar para conferir no celular">
                      <Smartphone className="h-4 w-4" /> Salvar p/ celular
                    </Button>
                  )}
                  <Button loading={busy} disabled={applyAllocations.length === 0 || !allConfirmed} onClick={confirmar}>
                    <Check className="h-4 w-4" /> Confirmar
                  </Button>
                </div>
              </div>

              {!allConfirmed && pendingPallets.size > 0 && (
                <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-700">
                  📷 Confira o QR de cada palete para liberar o “Confirmar” (evita tirar do palete errado).
                </div>
              )}

              {actionRows.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm font-medium text-green-700">✓ Tudo certo — o fracionamento já cobre toda a separação. Nada a buscar no armazém.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {actionRows.map(({ r, ri }) => (
                    <div key={ri} className="px-4 py-4">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[11px] text-gray-400">SKU {r.sku}</span>
                        <span className="text-base font-semibold text-gray-900">{r.name ?? '—'}</span>
                      </div>

                      {r.allocations.length > 0 ? (
                        <div className="mt-2 space-y-2">
                          {r.allocations.map((a, ai) => {
                            const done = confirmed.has(a.palletId)
                            return (
                              <div key={ai} className={cn('flex items-center gap-3 rounded-xl border px-3 py-2.5', done ? 'border-green-300 bg-green-50' : 'border-blue-100 bg-blue-50/60')}>
                                <Package className={cn('h-6 w-6 shrink-0', done ? 'text-green-600' : 'text-blue-600')} />
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2 text-sm">
                                    <span className="text-gray-600">Traga</span>
                                    <input type="number" min={0} inputMode="numeric"
                                      className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-center text-lg font-bold focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                      value={a.boxes} onChange={e => setBoxes(ri, ai, Number(e.target.value))} />
                                    <span className="font-semibold text-gray-800">caixas</span>
                                    {r.unitsPerBox ? <span className="text-xs text-gray-400">= {a.boxes * r.unitsPerBox} un</span> : null}
                                  </div>
                                  <div className="mt-0.5 truncate text-xs text-gray-500">
                                    do palete <span className="font-mono font-semibold text-gray-700">{a.palletCode}</span>
                                    {a.lotNumber ? <> · lote {a.lotNumber}</> : null}
                                    {a.expiryDate ? <> · <span className="text-amber-600">val {fmtDate(a.expiryDate)}</span></> : null}
                                  </div>
                                </div>
                                {a.boxes > 0 && (done ? (
                                  <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-green-700"><ShieldCheck className="h-4 w-4" /> Conferido</span>
                                ) : (
                                  <button type="button" onClick={() => { setScanStatus(null); setScanFor({ palletId: a.palletId, palletCode: a.palletCode }) }}
                                    className="flex shrink-0 items-center gap-1 rounded-lg border border-blue-400 px-2.5 py-2 text-xs font-medium text-blue-700 active:bg-blue-100">
                                    <Camera className="h-4 w-4" /> Conferir
                                  </button>
                                ))}
                              </div>
                            )
                          })}
                          {r.shortBoxes > 0 && (
                            <p className="flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> Faltam {r.shortBoxes} caixa(s) no armazém para cobrir tudo.</p>
                          )}
                        </div>
                      ) : r.warning === 'no_units_per_box' ? (
                        <p className="mt-1 flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> Cadastre "unidades por caixa" no produto para calcular as caixas.</p>
                      ) : r.warning === 'insufficient_reserve' ? (
                        <p className="mt-1 flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> Sem caixas deste produto no armazém.</p>
                      ) : r.warning === 'not_found' ? (
                        <p className="mt-1 flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="h-3.5 w-3.5" /> SKU não encontrado no cadastro.</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              {coveredRows.length > 0 && (
                <div className="border-t border-gray-100">
                  <button onClick={() => setShowCovered(v => !v)}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-xs text-gray-500 hover:bg-gray-50">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>{coveredRows.length} produto(s) já têm no fracionamento — nada a buscar</span>
                    <ChevronDown className={cn('ml-auto h-4 w-4 transition-transform', showCovered && 'rotate-180')} />
                  </button>
                  {showCovered && (
                    <ul className="space-y-1 px-4 pb-3">
                      {coveredRows.map(r => (
                        <li key={r.sku} className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="font-mono text-gray-400">{r.sku}</span>
                          <span className="flex-1 truncate">{r.name}</span>
                          <span className="text-gray-400">tem {r.fracionamento} · precisa {r.demanda}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )
      })()}

      {scanFor && (
        <CameraScan hint={`Bipe o QR do palete ${scanFor.palletCode}`} status={scanStatus}
          onScan={onScanPallet} onClose={() => setScanFor(null)} />
      )}
    </div>
  )
}

// ── Aba: fracionamento negativo ────────────────────────────────────────────────
//
// A caixa desceu do armazém na mão e ninguém registrou o abastecimento: a venda
// baixa do fracionamento, a entrada nunca aconteceu, e o saldo fura para baixo.
// Negativo não é só feio — ele infla o déficit da próxima onda e faz a triagem
// por disponibilidade recusar pedido que dava para atender.

function NegativosTab({ tenantId }: { tenantId: string }) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const qc = useQueryClient()
  const [aplicando, setAplicando] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const { data: linhas = [], isLoading, refetch } = useQuery<RegularizacaoLinha[]>({
    queryKey: ['negativos', tenantId],
    queryFn: async () => (await api.get('/replenishment/negativos', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const regularizaveis = linhas.filter(l => l.caixas > 0)
  const totalCaixas = regularizaveis.reduce((t, l) => t + l.caixas, 0)
  const totalUnidades = regularizaveis.reduce((t, l) => t + l.unidades, 0)
  const semLastro = linhas.filter(l => l.semLastro > 0)

  async function aplicar() {
    if (!regularizaveis.length) return
    if (!window.confirm(
      `Regularizar ${regularizaveis.length} produto(s)?\n\n` +
      `${totalCaixas} caixa(s) saem dos paletes e entram no fracionamento — ${totalUnidades} unidade(s).\n` +
      `O total da empresa NÃO muda: é o abastecimento que faltou registrar. A recontagem confirma.`
    )) return
    setAplicando(true); setMsg(null); setErro(null)
    try {
      const { data: r } = await api.post('/replenishment/negativos/regularizar', { tenantId, confirmar: true })
      setMsg(`${r.produtosRegularizados} produto(s) regularizado(s) · ${r.caixasMovidas} caixa(s) · ${r.unidadesMovidas} unidade(s).`)
      refetch()
      qc.invalidateQueries({ queryKey: ['consolidated', tenantId] })
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setErro(err.response?.data?.title ?? 'Falha ao regularizar')
    } finally { setAplicando(false) }
  }

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-400">Conferindo o fracionamento…</p>

  if (!linhas.length) {
    return (
      <Card><CardContent className="py-8 text-center">
        <p className="text-sm text-green-700">✓ Nenhum produto negativo no fracionamento.</p>
      </CardContent></Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <p className="font-semibold">{linhas.length} produto(s) com saldo negativo no fracionamento.</p>
        <p className="mt-1 text-amber-800">
          Saldo negativo quase sempre quer dizer que a caixa desceu do armazém sem o abastecimento
          ter sido registrado. A regularização <b>refaz o movimento que faltou</b>: tira caixa do
          palete e põe no fracionamento. O total da empresa não muda — muda o lugar.
        </p>
        <p className="mt-1 text-amber-800">
          Abastecimento move <b>caixa inteira</b>: zerar um déficit de 5 unidades numa caixa de 12
          deixa 7 no fracionamento. É o que teria acontecido se alguém tivesse registrado na hora,
          e é o que a recontagem confirma.
        </p>
      </div>

      {msg && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">{msg}</div>}
      {erro && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{erro}</div>}

      <div className="flex flex-wrap items-center gap-4">
        <div><p className="text-xs text-gray-500">Dá para regularizar</p><p className="text-2xl font-bold text-gray-900">{regularizaveis.length}</p></div>
        <div><p className="text-xs text-gray-500">Caixas a mover</p><p className="text-2xl font-bold text-gray-900">{totalCaixas}</p></div>
        <div><p className="text-xs text-gray-500">Sem lastro no armazém</p><p className="text-2xl font-bold text-amber-600">{semLastro.length}</p></div>
        <div className="ml-auto">
          {isAdmin
            ? <Button loading={aplicando} disabled={!regularizaveis.length} onClick={aplicar}>
                <Check className="h-4 w-4" /> Regularizar {regularizaveis.length}
              </Button>
            : <span className="text-xs text-gray-400">só admin aplica</span>}
        </div>
      </div>

      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left">SKU</th>
              <th className="px-4 py-2 text-left">Produto</th>
              <th className="px-4 py-2 text-right">Saldo</th>
              <th className="px-4 py-2 text-right">Faltam</th>
              <th className="px-4 py-2 text-left">De onde vem</th>
              <th className="px-4 py-2 text-right">Fica com</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(l => (
              <tr key={l.productId} className="border-b border-gray-100 align-top">
                <td className="px-4 py-2 font-mono text-xs">{l.sku}</td>
                <td className="px-4 py-2">
                  {l.name ?? '—'}
                  {l.aviso && <p className="mt-0.5 text-xs text-amber-700">{l.aviso}</p>}
                </td>
                <td className="px-4 py-2 text-right font-semibold text-red-600">{l.saldo}</td>
                <td className="px-4 py-2 text-right">{l.faltam}</td>
                <td className="px-4 py-2 text-xs text-gray-600">
                  {l.allocations.length === 0
                    ? <span className="text-gray-400">—</span>
                    : l.allocations.map((a, i) => (
                      <div key={a.palletItemId}>
                        <span className="text-gray-400">{i + 1}.</span> <b>{a.boxes} cx</b> do palete{' '}
                        <span className="font-mono">{a.palletCode}</span>
                        {a.sequencia != null ? <span className="text-gray-400"> (nº {a.sequencia})</span> : null}
                        {a.motivo ? <span className="text-gray-400"> · {a.motivo}</span> : null}
                      </div>
                    ))}
                </td>
                <td className={cn('px-4 py-2 text-right font-semibold', l.saldoFinal < 0 ? 'text-red-600' : 'text-green-700')}>
                  {l.saldoFinal}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      {semLastro.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
          <b>{semLastro.length} produto(s) o armazém não cobre.</b> Se não há palete, a mercadoria
          saiu de outro lugar — e adivinhar de onde seria inventar estoque. Esses continuam
          negativos de propósito, até a recontagem dizer o que realmente existe na prateleira.
        </div>
      )}
    </div>
  )
}

// ── Aba: Estoque consolidado ───────────────────────────────────────────────────

function ConsolidadoTab({ tenantId }: { tenantId: string }) {
  const { data: rows = [], isLoading } = useQuery<Consolidado[]>({
    queryKey: ['consolidated', tenantId],
    queryFn: async () => (await api.get('/replenishment/consolidated', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  return (
    <Card>
      <CardContent className="p-0">
        {isLoading ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">Carregando…</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">Nenhum produto.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">SKU</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Produto</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">Armazém (caixas)</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">Fracionamento</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">Total de venda</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Validade +próx.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => (
                  <tr key={r.productId}>
                    <td className="px-4 py-2 font-mono font-medium">{r.sku}</td>
                    <td className="px-4 py-2 text-gray-600">{r.name}</td>
                    <td className="px-4 py-2 text-right font-mono text-gray-600">
                      {r.reservaCaixas} cx <span className="text-gray-400">· {r.reservaUnidades} un</span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{r.fracionamento}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{r.total}</td>
                    <td className="px-4 py-2">
                      {r.nearestExpiry ? <span className="font-mono text-xs text-amber-600">{r.nearestExpiry}</span> : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
